import type {
  Ticket,
  TicketStatus,
  TicketPriority,
  TicketMessage,
  Department,
} from '../types/ticket.types';
import { supabase } from '@/shared/lib/supabase';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';

/**
 * Customer-facing ticket service. Reads via RLS (customer sees own tickets);
 * mutations through Edge Functions (ticket-create, ticket-reply).
 */

interface TicketRow {
  id: string;
  number: string;
  customer_id: string;
  department_id: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  related_cpanel_username: string | null;
  related_domain_name: string | null;
  related_invoice_id: string | null;
  assigned_staff_id: string | null;
  last_replied_at: string | null;
  last_replied_by_kind: 'customer' | 'staff' | 'system' | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

const TICKET_COLS =
  'id, number, customer_id, department_id, subject, status, priority, related_cpanel_username, related_domain_name, related_invoice_id, assigned_staff_id, last_replied_at, last_replied_by_kind, closed_at, created_at, updated_at';

const mapRowToTicket = (row: TicketRow): Ticket => ({
  id: row.id,
  customerId: row.customer_id,
  number: row.number,
  departmentId: row.department_id,
  subject: row.subject,
  status: row.status,
  priority: row.priority,
  openedAt: row.created_at,
  lastReplyAt: row.last_replied_at ?? row.updated_at ?? row.created_at,
  lastReplyByKind: row.last_replied_by_kind,
  assignedStaffId: row.assigned_staff_id ?? undefined,
  relatedCpanelUsername: row.related_cpanel_username ?? undefined,
  relatedDomainName: row.related_domain_name ?? undefined,
  relatedInvoiceId: row.related_invoice_id ?? undefined,
});

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

async function invokeEdgeFunction<TRes>(
  name: string,
  body: Record<string, unknown>
): Promise<TRes> {
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>(name, {
    body,
    headers: { 'Idempotency-Key': crypto.randomUUID() },
  });
  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.clone === 'function') {
      try {
        const parsed = (await ctx.clone().json()) as ApiError;
        if (parsed.code && parsed.message) {
          throw new EdgeFunctionError(parsed.code, parsed.message, ctx.status, parsed.details);
        }
      } catch (parseErr) {
        if (parseErr instanceof EdgeFunctionError) throw parseErr;
      }
    }
    throw new Error(error.message);
  }
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const apiErr = data as ApiError;
    throw new EdgeFunctionError(apiErr.code, apiErr.message, 200, apiErr.details);
  }
  return data as TRes;
}

export const ticketService = {
  async list(): Promise<Ticket[]> {
    const { data, error } = await supabase
      .from('ticket')
      .select(TICKET_COLS)
      .order('last_replied_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });
    if (error) throw new Error(`Could not load tickets: ${error.message}`);
    return (data ?? []).map((r) => mapRowToTicket(r as TicketRow));
  },

  async getById(id: string): Promise<Ticket | null> {
    const { data, error } = await supabase
      .from('ticket')
      .select(TICKET_COLS)
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(`Could not load ticket: ${error.message}`);
    return data ? mapRowToTicket(data as TicketRow) : null;
  },

  async listMessages(ticketId: string): Promise<TicketMessage[]> {
    const { data, error } = await supabase
      .from('ticket_message')
      .select('id, ticket_id, author_kind, author_id, body, is_internal_note, created_at')
      .eq('ticket_id', ticketId)
      .eq('is_internal_note', false)
      .order('created_at', { ascending: true });
    if (error) throw new Error(`Could not load messages: ${error.message}`);
    return (data ?? []).map((r) => {
      const row = r as {
        id: string;
        ticket_id: string;
        author_kind: 'customer' | 'staff' | 'system';
        author_id: string | null;
        body: string;
        is_internal_note: boolean;
        created_at: string;
      };
      return {
        id: row.id,
        ticketId: row.ticket_id,
        authorKind: row.author_kind,
        authorId: row.author_id,
        body: row.body,
        isInternalNote: row.is_internal_note,
        createdAt: row.created_at,
      };
    });
  },

  async listDepartments(): Promise<Department[]> {
    const { data, error } = await supabase
      .from('department')
      .select('id, name, sla_hours, is_active')
      .eq('is_active', true)
      .order('name', { ascending: true });
    if (error) throw new Error(`Could not load departments: ${error.message}`);
    return (data ?? []).map((d) => {
      const r = d as { id: string; name: string; sla_hours: number };
      return { id: r.id, name: r.name, slaHours: r.sla_hours };
    });
  },

  async create(input: {
    department_id: string;
    subject: string;
    body: string;
    priority?: TicketPriority;
    related_cpanel_username?: string;
    related_domain_name?: string;
    related_invoice_id?: string;
  }): Promise<{ ok: true; ticket_id: string; ticket_number: string }> {
    return invokeEdgeFunction('ticket-create', input as unknown as Record<string, unknown>);
  },

  async reply(input: {
    ticket_id: string;
    body: string;
  }): Promise<{ ok: true; message_id: string }> {
    return invokeEdgeFunction('ticket-reply', input as unknown as Record<string, unknown>);
  },
};
